// metrics.tsx
import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import type { MetricSnapshot } from "../API/Models";
import { formatExactDate } from "../Helper/dates";
import { useStatsData } from "../hooks/useStatsData";

type MetricKey = keyof MetricSnapshot;
type MetricKind = "count" | "ratio" | "bytes";

const METRIC_OPTIONS: Array<{ key: MetricKey; label: string; kind?: MetricKind }> = [
	{ key: "pages", label: "Pages" },
	{ key: "pagesCommuniques", label: "Communiqués" },
	{ key: "pagesStatistiques", label: "Statistiques" },
	{ key: "pagesPublications", label: "Publications" },
	{ key: "pagesEvenements", label: "Événements" },
	{ key: "mediaAssets", label: "Media Assets" },
	{ key: "users", label: "Users" },
	{ key: "sitemapEntries", label: "Sitemap Entries" },
	{ key: "logs", label: "Logs" },
	{ key: "storageObjects", label: "Storage Objects" },
	{ key: "storageFolders", label: "Storage Folders" },
	{ key: "storageBytesTotal", label: "Storage Total", kind: "bytes" },
	{ key: "storageBytesImages", label: "Storage – Images", kind: "bytes" },
	{ key: "storageBytesPdf", label: "Storage – PDF", kind: "bytes" },
	{ key: "storageBytesDocuments", label: "Storage – Documents", kind: "bytes" },
	{ key: "storageBytesOther", label: "Storage – Other", kind: "bytes" },
	{ key: "databaseBytes", label: "Database Size", kind: "bytes" },
	{ key: "migrations", label: "Migrations" },
	{ key: "migrationsRedirected", label: "Migration Redirected" },
	{ key: "migrationsSupported", label: "Migration Supported" },
	{ key: "migrationsBroken", label: "Migration Broken" },
	{ key: "migrationsSupportRatio", label: "Migration Support Ratio", kind: "ratio" }
];

const numberFormatter = new Intl.NumberFormat();

const formatBytes = (value: number): string => {
	if (!Number.isFinite(value) || value <= 0) return "0 B";
	const units = ["B", "KB", "MB", "GB", "TB"];
	const magnitude = Math.min(units.length - 1, Math.floor(Math.log(value) / Math.log(1024)));
	const scaled = value / Math.pow(1024, magnitude);
	const digits = scaled >= 100 || magnitude === 0 ? 0 : scaled >= 10 ? 1 : 2;
	return `${scaled.toFixed(digits)} ${units[magnitude]}`;
};
const cardColors = ["#0ea5e9", "#10b981", "#f97316", "#6366f1", "#f43f5e", "#14b8a6", "#facc15", "#8b5cf6"];

/** Site metrics dashboard with statistics cards and time-series charts. */
const Metrics: React.FC = () => {
	const {
		stats,
		history,
		loading,
		error,
		isRefreshing,
		refresh
	} = useStatsData();

	const timeline = useMemo(
		() =>
			history.map((point, index) => {
				const label = point.date && !Number.isNaN(new Date(point.date).getTime()) ? formatExactDate(point.date) : `Snapshot ${index + 1}`;
				return {
					label,
					values: point.snapshot
				};
			}),
		[history]
	);

	const cards = useMemo(() => {
		const previousIndex = timeline.length - 2;
		const latestIndex = timeline.length - 1;
		return METRIC_OPTIONS.map((metric, index) => {
			const currentValue = stats?.[metric.key] ?? 0;
			const previousValue = latestIndex >= 0 ? timeline[previousIndex]?.values?.[metric.key] ?? null : null;
			const delta = previousValue !== null ? currentValue - previousValue : null;
			const percentChange = previousValue && previousValue !== 0 ? (delta! / previousValue) * 100 : null;
			const series = timeline.map((point) => point.values?.[metric.key] ?? 0);
			const categories = timeline.map((point) => point.label);
			return {
				metric,
				color: cardColors[index % cardColors.length],
				currentValue,
				delta,
				percentChange,
				series,
				categories
			};
		});
	}, [stats, timeline]);

	const buildChartOptions = (color: string, categories: string[], kind: MetricKind = "count"): ApexOptions => ({
		chart: {
			type: "area",
			height: 200,
			toolbar: { show: false },
			zoom: { enabled: false }
		},
		stroke: {
			width: 3,
			curve: "smooth"
		},
		fill: {
			type: "gradient",
			gradient: {
				shadeIntensity: 0.3,
				opacityFrom: 0.4,
				opacityTo: 0.05,
				stops: [0, 90, 100]
			}
		},
		dataLabels: { enabled: false },
		grid: { borderColor: "#e2e8f0", strokeDashArray: 3 },
		xaxis: {
			categories,
			labels: {
				show: categories.length <= 8,
				style: { colors: "#94a3b8" }
			},
			axisBorder: { color: "#e2e8f0" },
			axisTicks: { color: "#e2e8f0" }
		},
		yaxis: {
			labels: {
				formatter: (value: number) =>
					kind === "bytes" ? formatBytes(value) : kind === "ratio" ? `${(value * 100).toFixed(1)}%` : numberFormatter.format(value),
				style: { colors: "#94a3b8" }
			}
		},
		tooltip: {
			y: {
				formatter: (value: number) =>
					kind === "bytes" ? formatBytes(value) : kind === "ratio" ? `${(value * 100).toFixed(1)}%` : numberFormatter.format(value)
			}
		},
		colors: [color]
	});

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full space-y-8">
				<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Metrics</p>
						<h1 className="text-2xl font-semibold text-slate-900">Repository insights</h1>
					</div>
					<button
						type="button"
						onClick={refresh}
						disabled={isRefreshing}
						className="ml-auto inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
					>
						{isRefreshing ? (
							<>
								<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
								Refreshing
							</>
						) : (
							"Refresh data"
						)}
					</button>
				</div>

				{error && (
					<div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
						{error}
					</div>
				)}

				<section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
					<header>
						<p className="text-sm font-medium uppercase tracking-wide text-slate-500">Historical metrics</p>
					</header>

					{loading ? (
						<div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
							{Array.from({ length: 6 }).map((_, index) => (
								<div key={index} className="h-64 animate-pulse rounded-2xl border border-slate-100 bg-slate-50" />
							))}
						</div>
					) : timeline.length === 0 ? (
						<div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center text-slate-500">
							<p className="text-lg font-semibold text-slate-900">No history available</p>
							<p className="mt-2 text-sm text-slate-500">Keep the sync service running to capture historical snapshots.</p>
						</div>
					) : (
						<div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
							{cards.map((card) => {
								const kind: MetricKind = card.metric.kind ?? "count";
								const formatValue = (n: number): string => {
									if (kind === "ratio") return `${(n * 100).toFixed(1)}%`;
									if (kind === "bytes") return formatBytes(n);
									return numberFormatter.format(n);
								};
								const formattedValue = formatValue(card.currentValue);
								const formattedDelta =
									card.delta !== null ? formatValue(Math.abs(card.delta)) : null;
								return (
									<div key={card.metric.key} className="flex flex-col rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
										<div className="flex items-start justify-between">
											<div>
												<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.metric.label}</p>
												<p className="mt-2 text-3xl font-bold text-slate-900">{formattedValue}</p>
											</div>
											{card.delta !== null && (
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
														card.delta > 0
															? "bg-emerald-50 text-emerald-700"
															: card.delta < 0
																? "bg-rose-50 text-rose-700"
																: "bg-slate-100 text-slate-600"
													}`}
												>
													{card.delta > 0 ? "▲" : card.delta < 0 ? "▼" : "●"} {formattedDelta}{" "}
													{card.percentChange !== null && (
														<span className="ml-1 text-[11px]">
															({card.percentChange > 0 ? "+" : ""}
															{card.percentChange.toFixed(1)}%)
														</span>
													)}
												</span>
											)}
										</div>
										<div className="mt-4 flex-1">
											<Chart
												options={buildChartOptions(card.color, card.categories, kind)}
												series={[{ name: card.metric.label, data: card.series }]}
												type="area"
												height={220}
											/>
										</div>
									</div>
								);
							})}
						</div>
					)}
				</section>
			</div>
		</div>
	);
};

export default Metrics;

